import { readArray, readBool, readEnum, readId, readInt, readNumber, readString } from '@/domain/format/validate';

/**
 * Parameter readers for commands. These are the same defensive readers used for
 * loading files, reused deliberately: a command from a script, a plugin or a
 * future AI assistant is exactly as untrusted as a file from disk.
 */

/**
 * True when a parameter was not supplied.
 *
 * `null` and `undefined` mean the same thing here — "leave this alone". A
 * caller building a record from typed fields naturally writes `null` for the
 * ones it is not setting, and JSON has no `undefined` at all, so treating them
 * differently would make a command behave differently depending on how it was
 * spelled.
 */
export function omitted(value: unknown): boolean {
  return value === undefined || value === null;
}

export function idParam(raw: unknown): string | null {
  return readId(raw);
}

export function idsParam(raw: unknown, max = 20000): string[] | null {
  if (!Array.isArray(raw)) return null;
  const ids = readArray(raw).slice(0, max).map(readId);
  if (ids.some((id) => id === null)) return null;
  return ids as string[];
}

export function numberParam(raw: unknown, min: number, max: number, fallback?: number): number | null {
  if (raw === undefined || raw === null) return fallback ?? null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return readNumber(n, n, min, max);
}

export function intParam(raw: unknown, min: number, max: number, fallback?: number): number | null {
  const n = numberParam(raw, min, max, fallback);
  return n === null ? null : readInt(n, n, min, max);
}

export function boolParam(raw: unknown, fallback?: boolean): boolean | null {
  if (typeof raw === 'boolean') return raw;
  return fallback ?? null;
}

export function stringParam(raw: unknown, maxLength = 128, fallback?: string): string | null {
  if (typeof raw !== 'string') return fallback ?? null;
  return readString(raw, fallback ?? '', maxLength) || (fallback ?? null);
}

export function enumParam<T extends string>(raw: unknown, allowed: readonly T[], fallback?: T): T | null {
  if (typeof raw !== 'string') return fallback ?? null;
  if (!(allowed as readonly string[]).includes(raw)) return fallback ?? null;
  return readEnum(raw, allowed, (fallback ?? allowed[0]) as T);
}

export function optionalBool(raw: unknown, fallback: boolean): boolean {
  return readBool(raw, fallback);
}
